import React, { CSSProperties, useState, useEffect, useMemo } from "react";
import moment from "moment";
import {
  concat,
  debounce,
  difference,
  intersection,
  omit,
  reject,
  sortBy,
} from "lodash";
import { catchAsyncStacktrace } from "auto-trace";

import { CpButton } from "@components";
import { defaultDateFormat, filterTypes } from "./column.helper";
import DynamicFilter from "./filter/dynamic-filter";
import FixedFilter from "./filter/fixed-filter";
import DateFilter from "./filter/date-filter";
import DateRangeFilter from "./filter/date-range-filter";
import { RelativeDateRangeFilter } from "./filter/relative-date-range-filter";
import { defaultDateFilterRanges } from "./filter/date-filter.helper";

import type {
  Column,
  FilterCallResult,
  FilterContext,
  FilterContextFilters,
  FilterSelections,
  FilterValue,
  LegacyEvent,
} from "./cp-table-header-menu.types";

import styles from "./menu-content.styles.css";

export type CpDateRangeSelectorProps = {
  filterContext: FilterContext;
  column: Column;
  getDynamicFilters?: (
    filterName: string,
    columnFilters: FilterContextFilters,
    sortFilterList?: boolean,
    filterSearchValue?: string | false,
  ) => FilterCallResult;
  getFixedFilters?: (column: Column) => FilterValue[];
  onCancel?: () => void;
  setColumnMenuShown: (shown: boolean) => void;
  menuStyles?: CSSProperties;
  hideSortOptions?: boolean;
  hideClear?: boolean;
};

export const CpDateRangeSelector = ({
  filterContext,
  column,
  getDynamicFilters,
  getFixedFilters,
  onCancel,
  setColumnMenuShown,
  menuStyles,
  hideSortOptions,
  hideClear,
}: CpDateRangeSelectorProps) => {
  const [availableFilterValues, setAvailableFilterValues] = useState<
    FilterValue[]
  >([]);
  const [allFilterValues, setAllFilterValues] = useState<FilterValue[] | null>(
    null,
  );
  const [selections, setSelections] = useState<FilterSelections>({
    ...filterContext.filters[column.sortParam],
  });
  const [filterSearchValue, setFilterSearchValue] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(false);
  const [initialSearch, setInitialSearch] = useState(false);
  const [searching, setSearching] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const isDateRangeFilter =
    column.filterType === filterTypes.DateRange ||
    column.filterType === filterTypes.RelativeDateRange;

  useEffect(() => {
    const currentFilter = filterContext.filters[column.sortParam];
    if (
      currentFilter?.before?.includes("-") ||
      currentFilter?.after?.includes("-")
    ) {
      // Check if the value was passed in, and if it's in 'YYYY-MM-DD' format
      applyDateRangeFilter(currentFilter.filter_params, currentFilter); // If so, we need to format it to 'MM/DD/YYYY' for the rest of the calculations, if not, the selections state is already in the format the component needs ('MM/DD/YYYY')
    }
    /* Add missing deps and verify it doesn't break: applyDateRangerFilter (memoize), column.sortParam */
  }, [filterContext]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchDynamicFilter = () => setSearchTrigger(true);

  const debounceGetDynamicFilters = useMemo(
    () => debounce(searchDynamicFilter, 500),
    [],
  );

  useEffect(() => {
    if (!searchTrigger) return;
    let columnFilters = { ...filterContext.filters };
    columnFilters = omit(columnFilters, column.sortParam);

    const handleResult = (result: FilterValue[]) => {
      if (result) {
        // check if the array has a null entry, then remove it
        let allFilterValues = reject(result, ["label", null]);

        if (column.sortFilterList) {
          allFilterValues = sortBy(allFilterValues, (value) =>
            value.label?.toLowerCase(),
          );
        }
        setAvailableFilterValues(
          filterSearchValue
            ? allFilterValues.filter(
                (value) =>
                  value.label &&
                  value.label
                    .toString()
                    .toLowerCase()
                    .includes(filterSearchValue),
              )
            : allFilterValues,
        );
        setAllFilterValues(allFilterValues);
        setSearchTrigger(false);
        setSearching(false);
      }
    };

    const filterCall = getDynamicFilters?.(
      column.filterName,
      columnFilters,
      column.sortFilterList,
      column.showSearch && filterSearchValue.trim(),
    );

    if (typeof filterCall?.then === "function") {
      filterCall.then(handleResult).catch(catchAsyncStacktrace());
    } else if (typeof filterCall?.subscribe === "function") {
      filterCall.subscribe(handleResult, catchAsyncStacktrace());
    }

    return () => {
      if (typeof filterCall?.unsubscribe === "function") {
        filterCall.unsubscribe();
      }
    };
    /* Tons of deps are missing here, remove the disable comment and run lint to see */
  }, [searchTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (column.filterType === filterTypes.Dynamic) {
      if (column.showFilterList) {
        if (!initialSearch) {
          setSearchTrigger(true);
          setInitialSearch(true);
          setSearching(true);
        } else {
          setSearching(true);
          debounceGetDynamicFilters();
        }
      }
    } else if (column.filterType === filterTypes.Fixed) {
      const availableFilters = getFixedFilters
        ? getFixedFilters(column)
        : [...(column.filterValues || [])];

      setAvailableFilterValues(availableFilters);
      setAllFilterValues(availableFilters);
    }
    /* Add missing deps and verify it doesn't break: debounceGetDynamicFilters, initialSearch, getFixedFilters (memoize or store in ref) */
  }, [column, filterSearchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterSelections = (
    e: React.ChangeEvent<HTMLInputElement>,
    applyImmediately?: boolean,
  ) => {
    const searchValue = e.target.value.toString().toLowerCase().trimStart();
    setFilterSearchValue(searchValue);

    if (applyImmediately) {
      applyFilter(e, searchValue, false);
    } else {
      if (!allFilterValues) return;

      setAvailableFilterValues(
        allFilterValues.filter(
          (value) =>
            value.label &&
            value.label.toString().toLowerCase().includes(searchValue),
        ),
      );
    }
  };

  const applySelections = () => {
    filterContext.applyFilter(column.sortParam, selections || 0);
    setColumnMenuShown(false);
  };

  const clearSelections = () => {
    setSelections({});
    setAvailableFilterValues(allFilterValues || []);
    setFilterSearchValue("");
    setDisableSave(false);
  };

  const applySort = (order: "asc" | "desc", e: React.MouseEvent) => {
    e.stopPropagation();

    setSelections({
      ...selections,
      order: selections && selections.order === order ? undefined : order,
    });
  };

  const applyFilter = (
    e: React.MouseEvent | React.ChangeEvent<HTMLInputElement>,
    filter: string | string[],
    allowMultiple?: boolean,
    selectionRules?: (
      filter: string | string[],
      filterParams: string[],
    ) => string[],
  ) => {
    e.stopPropagation();

    const newSelections = { ...selections };

    if (!newSelections.filter_params) {
      newSelections.filter_params = [];
    }
    const filterIsArray = Array.isArray(filter);
    const isSelected = filterIsArray
      ? intersection(newSelections.filter_params, filter).length > 0
      : newSelections.filter_params.includes(filter);

    isSelected
      ? (newSelections.filter_params = difference(
          newSelections.filter_params,
          filterIsArray ? filter : [filter],
        ))
      : allowMultiple
        ? (newSelections.filter_params = concat(
            newSelections.filter_params,
            filter,
          ))
        : (newSelections.filter_params = [filter] as string[]);

    if (selectionRules) {
      newSelections.filter_params = selectionRules(
        filter,
        newSelections.filter_params,
      );
    }

    setSelections(newSelections);
  };

  const applyDateFilter = (evt: LegacyEvent<Date | null>) => {
    const selectedDate = moment(evt.detail).format(defaultDateFormat);
    setSelections({ ...selections, filter_params: selectedDate });
  };

  const applyDateRangeFilter = (
    selectedFilter: string | string[] | undefined,
    filter: {
      before?: moment.Moment | string | null;
      after?: moment.Moment | string | null;
    },
  ) => {
    let before, after; // Canopy date standard is 'YYYY-MM-DD' as of Feb 2020 but this component works with date as 'MM/DD/YYYY', the below code is overcomplicated because we are accepting either format
    if (filter.before && typeof filter.before === "string") {
      // if the before and after dates were passed in as 'YYYY-MM-DD', we need to format them to 'MM/DD/YYYY' for this component
      before = moment(filter.before, "YYYY-MM-DD").format(defaultDateFormat);
    } else if (filter.before) {
      before = (filter.before as moment.Moment).format(defaultDateFormat);
    }
    if (filter.after && typeof filter.after === "string") {
      // if the before and after dates were passed in as 'YYYY-MM-DD', we need to format them to 'MM/DD/YYYY' for this component
      after = moment(filter.after, "YYYY-MM-DD").format(defaultDateFormat);
    } else if (filter.after) {
      after = (filter.after as moment.Moment).format(defaultDateFormat);
    }
    if (before || after || selectedFilter) {
      setSelections({
        ...selections,
        filter_params: selectedFilter,
        before,
        after,
      });
    } else {
      setSelections({});
    }
  };

  const selectedFilters = selections.filter_params;

  return (
    <ul
      className="cps-dropdown-menu cps-padding-bottom-0"
      role="menu"
      style={menuStyles}
      onClick={(e) => e.stopPropagation()}
    >
      {!hideSortOptions && (
        <>
          <li
            className={`cps-cursor-pointer ${
              isDateRangeFilter
                ? `${styles.dateFilterWidth} cp-wt-semibold`
                : ""
            }`}
          >
            <div
              className={`${styles.spreadContents} cps-margin-left-16 cps-margin-right-16 cps-padding-top-8 cps-padding-bottom-8`}
              onClick={(evt) => applySort("asc", evt)}
              style={{ lineHeight: "20px" }}
            >
              <div
                style={{ whiteSpace: "nowrap" }}
                className={
                  selections.order === "asc" ? "cps-color-primary" : ""
                }
              >
                Sort
                <span className="cp-wt-semibold">
                  &nbsp;
                  {column.minSortValue} - {column.maxSortValue}
                </span>
              </div>
              <div style={{ minWidth: "25px", margin: "-3px" }}>
                {selections.order === "asc" && (
                  <i className="cps-color-primary cps-icon cps-icon-sm-check" />
                )}
              </div>
            </div>
          </li>
          <li
            className={`cps-cursor-pointer ${
              isDateRangeFilter
                ? `${styles.dateFilterWidth} cp-wt-semibold`
                : ""
            }`}
          >
            <div
              className={`${styles.spreadContents} cps-margin-left-16 cps-margin-right-16 cps-padding-top-8 cps-padding-bottom-8`}
              onClick={(evt) => applySort("desc", evt)}
              style={{ lineHeight: "20px" }}
            >
              <div
                style={{ whiteSpace: "nowrap" }}
                className={
                  selections.order === "desc" ? "cps-color-primary" : ""
                }
              >
                Sort
                <span className="cp-wt-semibold">
                  &nbsp;
                  {column.maxSortValue} - {column.minSortValue}
                </span>
              </div>
              <div style={{ minWidth: "25px", margin: "-3px" }}>
                {selections.order === "desc" && (
                  <i className="cps-color-primary cps-icon cps-icon-sm-check" />
                )}
              </div>
            </div>
          </li>
        </>
      )}

      {column.filterType === filterTypes.Dynamic && (
        <DynamicFilter
          filterValues={availableFilterValues}
          filterSelections={filterSelections}
          applyFilter={applyFilter}
          selections={
            Array.isArray(selectedFilters) ? selectedFilters : undefined
          }
          showSearch={column.showSearch}
          showFilterList={column.showFilterList}
          hideSortOptions={hideSortOptions}
          filterSearchValue={filterSearchValue}
          searchPlaceholder={column.searchPlaceholder}
          searching={searching}
        />
      )}
      {column.filterType === filterTypes.Fixed && (
        <FixedFilter
          filterValues={availableFilterValues}
          applyFilter={applyFilter}
          selections={
            Array.isArray(selectedFilters) ? selectedFilters : undefined
          }
          hideSortOptions={hideSortOptions}
          allowMultiple={column.allowMultiple}
        />
      )}
      {column.filterType === filterTypes.Date && (
        <DateFilter
          applyFilter={applyDateFilter}
          selectedDate={
            Array.isArray(selectedFilters)
              ? selectedFilters[0]
              : selectedFilters
          }
        />
      )}
      {column.filterType === filterTypes.DateRange && (
        <DateRangeFilter
          applyFilter={applyDateRangeFilter}
          filter={selections}
          dateRanges={column.dateRanges || defaultDateFilterRanges}
          hideSortOptions={hideSortOptions}
          setDisableSave={setDisableSave}
        />
      )}
      {column.filterType === filterTypes.RelativeDateRange && (
        <RelativeDateRangeFilter
          applyFilter={applyDateRangeFilter}
          filter={selections}
          dateRanges={column.dateRanges || defaultDateFilterRanges}
          hideSortOptions={hideSortOptions}
        />
      )}
      {column.filterType === filterTypes.Custom &&
        column.customFilter &&
        column.customFilter({
          applyFilter: applyFilter,
          selections: selections,
        })}
      {column.filterType === filterTypes.None && <div />}
      <div className={`${styles.applyFilter} ${styles.spreadContents}`}>
        <div style={{ display: "inline-flex" }}>
          <CpButton
            btnType="primary"
            className="cp-mr-8"
            onClick={applySelections}
            disabled={disableSave}
          >
            Apply
          </CpButton>
          <CpButton btnType="flat" onClick={onCancel}>
            Cancel
          </CpButton>
        </div>
        {!hideClear && (
          <CpButton btnType="flat" onClick={clearSelections}>
            Clear
          </CpButton>
        )}
      </div>
    </ul>
  );
};
